import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../layout/AdminLayoutSewa";
import Swal from "sweetalert2";
import { adminDbklMonthlyReport } from "../../api";

function monthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function money(v) {
  return Number(v || 0).toFixed(2);
}

export default function DBLKMonthlyReport() {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState("perumahan");
  const [month, setMonth] = useState(monthStr());
  const [search, setSearch] = useState("");

  const [summary, setSummary] = useState({
    month,
    template,
    total_bills: 0,
    total_amount: 0,
    paid_bills: 0,
    unpaid_bills: 0,
    overdue_bills: 0,
    total_paid_amount: 0,
    total_overdue_amount: 0,
  });

  const [kawasanSummary, setKawasanSummary] = useState([]);
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminDbklMonthlyReport({ template, month });
      setSummary(res?.summary || {});
      setKawasanSummary(Array.isArray(res?.kawasan_summary) ? res.kawasan_summary : []);
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (error) {
      console.error(error);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: error?.response?.data?.error || "Gagal load laporan bulanan DBKL.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [template, month]);

  const filteredKawasan = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return kawasanSummary;

    return kawasanSummary.filter((r) =>
      [r.kawasan, r.kod_kawasan]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(q))
    );
  }, [kawasanSummary, search]);

  return (
    <AdminLayout>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Laporan Bulanan DBKL</h2>
            <p className="text-sm text-gray-500 mt-1">
              Snapshot keseluruhan bayaran, caj dan tunggakan; bulan ialah rujukan paparan.
            </p>
          </div>

          <button
            onClick={load}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-600">Template</label>
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              className="border rounded-md w-full p-2 text-sm bg-white"
            >
              <option value="perumahan">Perumahan</option>
              <option value="penjaja">Penjaja</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600">Bulan</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border rounded-md w-full p-2 text-sm bg-white"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600">Search Kawasan</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kawasan / Kod Kawasan"
              className="border rounded-md w-full p-2 text-sm"
            />
          </div>
        </div>

        {summary.calculation_basis === "CURRENT_SNAPSHOT" && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Data ini ialah snapshot semasa. Bulan yang dipilih tidak menapis sejarah transaksi.
          </div>
        )}

        <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="p-4 rounded border bg-blue-50">
            <div className="text-xs text-gray-600">JUMLAH BIL</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">{summary.total_bills || 0}</div>
          </div>

          <div className="p-4 rounded border bg-indigo-50">
            <div className="text-xs text-gray-600">JUMLAH CAJ</div>
            <div className="text-2xl font-bold text-indigo-700 mt-1">
              RM {money(summary.total_amount)}
            </div>
          </div>

          <div className="p-4 rounded border bg-green-50">
            <div className="text-xs text-gray-600">JUMLAH BAYARAN</div>
            <div className="text-2xl font-bold text-green-700 mt-1">
              RM {money(summary.total_paid_amount)}
            </div>
          </div>

          <div className="p-4 rounded border bg-red-50">
            <div className="text-xs text-gray-600">JUMLAH TUNGGAKAN</div>
            <div className="text-2xl font-bold text-red-700 mt-1">
              RM {money(summary.total_overdue_amount)}
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="p-4 rounded border bg-green-50">
            <div className="text-xs text-gray-600">PAID</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{summary.paid_bills || 0}</div>
          </div>

          <div className="p-4 rounded border bg-yellow-50">
            <div className="text-xs text-gray-600">UNPAID</div>
            <div className="text-2xl font-bold text-yellow-700 mt-1">{summary.unpaid_bills || 0}</div>
          </div>

          <div className="p-4 rounded border bg-red-50">
            <div className="text-xs text-gray-600">BERTUNGGAK</div>
            <div className="text-2xl font-bold text-red-700 mt-1">{summary.overdue_bills || 0}</div>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 border">Kawasan</th>
                <th className="text-left p-2 border">Kod</th>
                <th className="text-left p-2 border">Jumlah Bil</th>
                <th className="text-left p-2 border">Paid</th>
                <th className="text-left p-2 border">Unpaid</th>
                <th className="text-left p-2 border">Overdue</th>
                <th className="text-left p-2 border">Jumlah Caj (RM)</th>
                <th className="text-left p-2 border">Jumlah Bayaran (RM)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-3 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredKawasan.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-3 text-center text-gray-500">
                    Tiada data.
                  </td>
                </tr>
              ) : (
                filteredKawasan.map((r) => (
                  <tr key={r.kawasan} className="border-t hover:bg-gray-50">
                    <td className="p-2 border font-semibold">{r.kawasan}</td>
                    <td className="p-2 border">{r.kod_kawasan || "-"}</td>
                    <td className="p-2 border">{r.total_bills}</td>
                    <td className="p-2 border">{r.paid_bills}</td>
                    <td className="p-2 border">{r.unpaid_bills}</td>
                    <td className="p-2 border">{r.overdue_bills}</td>
                    <td className="p-2 border">{money(r.total_amount)}</td>
                    <td className="p-2 border">{money(r.paid_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-5">
          <div className="text-sm font-semibold text-gray-700 mb-2">Rekod Terperinci</div>
          <div className="max-h-80 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2 border">No Akaun</th>
                  <th className="text-left p-2 border">Unit</th>
                  <th className="text-left p-2 border">Kawasan</th>
                  <th className="text-left p-2 border">Tunggakan (RM)</th>
                  <th className="text-left p-2 border">Caj (RM)</th>
                  <th className="text-left p-2 border">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-3 text-center text-gray-500">
                      Tiada rekod.
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="p-2 border">{r.no_akaun || "-"}</td>
                      <td className="p-2 border">{r.nombor_unit || "-"}</td>
                      <td className="p-2 border">{r.kawasan || "-"}</td>
                      <td className="p-2 border">{money(r.tunggakan)}</td>
                      <td className="p-2 border">{money(r.caj_sewa)}</td>
                      <td className="p-2 border">
                        {r.status === "PAID" ? (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700">
                            PAID
                          </span>
                        ) : r.overdue ? (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700">
                            BERTUNGGAK
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">
                            UNPAID
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
